// Shared confirmation modal — asks for any admin password before a sensitive
// action (delete, void, etc). On success calls onConfirm({ adminId, adminName, password })
// so the caller knows who approved. Backend: POST /auth/verify-admin
//
// 2026-09-07 — the password is handed back as well. Verifying here only
// proves it to the browser; an action that must really be gated has to send
// it on and have its own route check it.
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FiX, FiLock, FiAlertTriangle } from 'react-icons/fi';
import { verifyAdmin } from '../services/api';

export default function AdminPasswordPrompt({ open, subject, actionLabel = 'Confirm Delete', onConfirm, onCancel }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPassword('');
      setError('');
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!password) { setError('Enter the admin password.'); return; }
    setBusy(true); setError('');
    try {
      const r = await verifyAdmin({ password });
      if (r.data?.ok) {
        onConfirm({ adminId: r.data.adminId, adminName: r.data.adminName, password });
      } else {
        setError('Invalid admin password.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed.');
    } finally {
      setBusy(false);
    }
  };

  return ReactDOM.createPortal((
    <div
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
      onKeyDown={(e) => { if (e.key === 'Escape' && !busy) onCancel(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <form
        onSubmit={submit}
        style={{ background: '#141924', borderRadius: 12, padding: 24, width: 420, maxWidth: 'calc(100vw - 32px)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid #2a3344' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#f59e0b' }}>
            <FiLock size={20} />
            <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, color: '#e8e6e0' }}>Admin Authorization Required</h2>
          </div>
          <button type="button" onClick={onCancel} disabled={busy} style={{ background: 'none', border: 'none', cursor: busy ? 'not-allowed' : 'pointer', color: '#94a1b8' }}>
            <FiX size={20} />
          </button>
        </div>

        {subject && (
          <div style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#f87171', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>You're about to delete</div>
            <div style={{ fontSize: 14, color: '#e8e6e0', fontWeight: 600 }}>{subject}</div>
          </div>
        )}

        <p style={{ fontSize: 13, color: '#94a1b8', margin: '0 0 12px' }}>
          Enter any administrator's password to confirm.
        </p>

        <label style={{ fontSize: 13, fontWeight: 600, color: '#e8e6e0', display: 'block', marginBottom: 6 }}>Admin Password</label>
        <input
          ref={inputRef}
          type="password"
          value={password}
          onChange={(e) => { setPassword(e.target.value); setError(''); }}
          disabled={busy}
          autoComplete="current-password"
          placeholder="••••••••"
          style={{ width: '100%', padding: '10px 12px', background: '#1c2230', color: '#e8e6e0', border: `1px solid ${error ? '#f87171' : '#2a3344'}`, borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none' }}
        />

        {error && (
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 6, color: '#f87171', fontSize: 13 }}>
            <FiAlertTriangle size={14} /> {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            style={{ padding: '9px 18px', background: 'transparent', color: '#94a1b8', border: '1px solid #2a3344', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer' }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || !password}
            style={{ padding: '9px 18px', background: busy || !password ? '#374151' : '#f59e0b', color: busy || !password ? '#94a1b8' : '#0b0f17', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: busy || !password ? 'not-allowed' : 'pointer' }}
          >
            {busy ? 'Verifying…' : actionLabel}
          </button>
        </div>
      </form>
    </div>
  ), document.body);
}
